import { Box, Container, Typography, Button, Grid, Stack } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";
import ContactUsForm from "../../components/ContactUsForm";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const contactInfoData = [
  {
    icon: "📍",
    title: "Visit Our Office",
    content: "6401 Eldorado Pkwy,",
    subContent: "STE 309, 311, 330, 411",
    extraContent: "McKinney, Texas 75070",
    link: "https://maps.app.goo.gl/sEztkGC9Uf1VPgEu8",
    linkText: "Get Directions",
  },
  {
    icon: "📧",
    title: "Email Us",
    content: "info@briozon.com",
    subContent: "hr@briozon.com",
    link: "mailto:info@briozon.com",
    linkText: "Send Email",
  },
  {
    icon: "📞",
    title: "Call Us",
    content: "+1 (469) 844-8747",
    subContent: "Mon - Fri: 9AM - 6PM CST",
    link: "tel:+14698448747",
    linkText: "Call Now",
  },
];

const quickLinksData = [
  { icon: "💼", title: "Careers", description: "Join our growing team", link: "/careers" },
  { icon: "🛠️", title: "Services", description: "Explore our solutions", link: "/services" },
  { icon: "📦", title: "Products", description: "View our products", link: "/products" },
  { icon: "🏢", title: "About Us", description: "Learn about Briozon", link: "/about" },
];

const Contact = () => {
  useDocumentTitle("Briozon - Contact Us");
  const { state: queryState } = useLocation();

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #e8f4f8 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(0, 128, 128, 0.1), rgba(0, 128, 128, 0.05))",
            top: -40,
            left: -40,
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(255, 165, 0, 0.1), rgba(255, 165, 0, 0.05))",
            bottom: 40,
            right: 80,
            animation: `${floatBall} 10s ease-in-out infinite`,
            animationDelay: "-3s",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(100, 149, 237, 0.15), rgba(100, 149, 237, 0.05))",
            top: "50%",
            right: "15%",
            animation: `${floatBall} 7s ease-in-out infinite`,
            animationDelay: "-5s",
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ animation: `${fadeUp} 0.8s ease-out` }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "var(--primary-color)",
                    fontWeight: 600,
                    letterSpacing: 2,
                    mb: 1,
                    display: "block",
                  }}
                >
                  GET IN TOUCH
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Let's Start a{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, var(--primary-color), var(--primary-color-2))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Conversation
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#555",
                    fontSize: { xs: "16px", sm: "17px", md: "18px" },
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  Have a question or want to work together? We're here to help you
                  transform your business with innovative technology solutions.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    href="tel:+14698448747"
                    sx={{
                      backgroundColor: "var(--primary-color)",
                      color: "#fff",
                      px: 3,
                      py: 1.2,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "var(--primary-color-2)",
                      },
                    }}
                  >
                    📞 Call Us Now
                  </Button>
                  <Button
                    variant="outlined"
                    href="mailto:info@briozon.com"
                    sx={{
                      borderColor: "var(--primary-color)",
                      color: "var(--primary-color)",
                      px: 3,
                      py: 1.2,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "rgba(0, 128, 128, 0.05)",
                        borderColor: "var(--primary-color)",
                      },
                    }}
                  >
                    📧 Email Us
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  animation: `${fadeUp} 0.8s ease-out`,
                  animationDelay: "0.2s",
                  animationFillMode: "both",
                }}
              >
                <Box
                  sx={{
                    fontSize: { xs: "8rem", md: "12rem" },
                    animation: `${pulse} 3s ease-in-out infinite`,
                  }}
                >
                  💬
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Ready to Transform Section */}
      <Box
        sx={{
          backgroundColor: "#fff",
          py: { xs: 8, md: 11 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle dot-grid background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(0,128,128,0.055) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            pointerEvents: "none",
          }}
        />
        {/* Soft glow orbs */}
        <Box
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-5%",
            width: 320,
            height: 320,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0,128,128,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-10%",
            left: "-5%",
            width: 280,
            height: 280,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0,128,128,0.05) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            {/* Left — heading + paragraph */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: "var(--primary-color)",
                  fontWeight: 700,
                  letterSpacing: "2.5px",
                  display: "block",
                  mb: 2,
                  fontSize: "12px",
                }}
              >
                WHY BRIOZON
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "26px", sm: "32px", md: "40px" },
                  color: "#1b1f33",
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                Ready to{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-2) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Transform
                </Box>{" "}
                Your Business?
              </Typography>
              <Typography
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.9,
                  fontSize: { xs: "15px", sm: "17px", md: "18px" },
                  textAlign: "justify",
                }}
              >
                AI-powered automation, intelligent scheduling engines, and smart
                analytics form the core of our solutions — helping businesses
                operate faster, smoother, and with greater precision. Whether you
                need appointment and reservation systems, IT consulting, or
                software development, our team of experts is ready to help you
                achieve your goals.
              </Typography>
            </Grid>

            {/* Right — 3 capability cards */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  {
                    icon: "⚡",
                    title: "AI-Powered Automation",
                    desc: "Intelligent systems that automate workflows, reduce manual effort, and accelerate business operations end-to-end.",
                  },
                  {
                    icon: "🎯",
                    title: "Smart Scheduling & Analytics",
                    desc: "Precision scheduling engines and data analytics that surface insights and drive decisions with measurable impact.",
                  },
                  {
                    icon: "🚀",
                    title: "Expert Software Development",
                    desc: "From IT consulting to full-scale custom development — solutions architected for reliability, scale, and growth.",
                  },
                ].map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: "18px",
                      background:
                        "linear-gradient(135deg, rgba(0,128,128,0.05) 0%, rgba(0,128,128,0.02) 100%)",
                      border: "1px solid rgba(0,128,128,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(7px)",
                        boxShadow: "0 6px 28px rgba(0,128,128,0.1)",
                        border: "1px solid rgba(0,128,128,0.22)",
                        background: "#fff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "13px",
                        background:
                          "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-2) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "22px",
                        flexShrink: 0,
                        boxShadow: "0 6px 18px rgba(0,128,128,0.28)",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "15px",
                          color: "#1b1f33",
                          mb: 0.5,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{ color: "#6b7490", fontSize: "14px", lineHeight: 1.7 }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Bottom highlight strip */}
          <Box
            sx={{
              mt: 6,
              p: { xs: 3, sm: 4 },
              borderRadius: "20px",
              background:
                "linear-gradient(135deg, rgba(0,128,128,0.07) 0%, rgba(0,128,128,0.03) 100%)",
              border: "1px solid rgba(0,128,128,0.13)",
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box sx={{ fontSize: "38px", flexShrink: 0 }}>🤝</Box>
            <Typography
              sx={{
                color: "#4c5472",
                fontSize: { xs: "15px", sm: "17px", md: "18px" },
                lineHeight: 1.85,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: "#1b1f33" }}>
                Reach out today
              </Box>{" "}
              and let's explore how{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-2) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontWeight: 700,
                }}
              >
                human expertise
              </Box>{" "}
              and{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, var(--primary-color-2) 0%, var(--primary-color) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  fontWeight: 700,
                }}
              >
                AI innovation
              </Box>{" "}
              can work together to move your business forward.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Info Cards + Form Section */}
      <Box sx={{ backgroundColor: "#f8fafc", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="flex-start">
            {/* Left - Contact Info Cards */}
            <Grid item xs={12} md={5}>
              <Grid container spacing={3} direction="column">
                {contactInfoData.map((item, index) => (
                  <Grid item key={index}>
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        minHeight: 100,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        animation: `${fadeUp} 0.6s ease-out`,
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: "both",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <Box sx={{ fontSize: "2rem", flexShrink: 0 }}>{item.icon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.2, fontSize: { xs: "18px", sm: "20px" } }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
                          {item.content}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {item.subContent}
                        </Typography>
                        {item.extraContent && (
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            {item.extraContent}
                          </Typography>
                        )}
                        <Button
                          component={item.link.startsWith("http") ? "a" : Link}
                          href={item.link.startsWith("http") || item.link.startsWith("mailto") || item.link.startsWith("tel") ? item.link : undefined}
                          to={!item.link.startsWith("http") && !item.link.startsWith("mailto") && !item.link.startsWith("tel") ? item.link : undefined}
                          target={item.link.startsWith("http") ? "_blank" : undefined}
                          size="small"
                          sx={{
                            color: "var(--primary-color)",
                            textTransform: "none",
                            fontWeight: 600,
                            p: 0,
                            minWidth: 0,
                            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                          }}
                        >
                          {item.linkText} →
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Right - Contact Form */}
            <Grid item xs={12} md={7}>
              <ContactUsForm subjectText={queryState?.subjectText} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Quick Links Section */}
      <Box sx={{ backgroundColor: "#f8fafc", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Explore More
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{ color: "#666", mb: 5 }}
          >
            Discover what Briozon has to offer
          </Typography>
          <Grid container spacing={3}>
            {quickLinksData.map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Link
                  to={item.link}
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box sx={{ fontSize: "2.5rem", mb: 1 }}>{item.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#333", mb: 0.5 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Link>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-2) 100%)",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#fff",
                mb: 2,
              }}
            >
              Let's Build Something Amazing Together
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Partner with Briozon for innovative technology solutions that drive
              growth and transform your business.
            </Typography>
            <Button
              component={Link}
              to="/services"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "var(--primary-color)",
                px: 4,
                py: 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              Explore Services
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
